import { useUnsavedPromptInModal } from '@common/UnsavedPromptInModal.hooks';
import { isPositiveNumber } from '@core/Primitives/number.services';
import { Button, ComponentType, LoadingButton, ModalBodyExtras, useModal } from '@gcl/react-core';
import { IProjectUserProfile } from '@userProfile/projectUserProfile.models';
import * as React from 'react';
import { IMemberFormProps, MemberForm } from './MemberForm';
import { EMemberFormKeys } from './memberModal.models';

export interface IUseMemberModalProps {
  onConfirm: (projectUserProfile?: IProjectUserProfile) => void;
  isShowTeams?: boolean;
  shouldIncludeHiddenRoles?: boolean;
}

export interface IRenderMemberModalProps extends Omit<IMemberFormProps, 'setIsFormDirty'> {
  isSubmitLoading?: boolean;
  isSubmitError?: boolean;
  isSubmitSuccess?: boolean;
}

export const useMemberModal = ({ onConfirm, isShowTeams, shouldIncludeHiddenRoles = false }: IUseMemberModalProps) => {
  const { openPortal, isOpen, closePortal: closeMemberPortal, renderModal } = useModal();
  const closePortal = React.useRef(closeMemberPortal);
  const renderMemberModal = ({
    projectUserProfile,
    isSubmitLoading = false,
    isSubmitError = false,
    isSubmitSuccess = false,
    ...memberFormProps
  }: IRenderMemberModalProps) => {
    const { useFormMethods } = memberFormProps;
    const { watch } = useFormMethods;

    const formStateProjectUserProfileId = watch(EMemberFormKeys.USER_PROFILE_ID);
    const { isFormDirty, setIsFormDirty, confirmUnsavedPrompt } = useUnsavedPromptInModal();

    closePortal.current = () => {
      useFormMethods.reset({});
      closeMemberPortal();
    };

    return (
      isOpen &&
      renderModal({
        onHide: () => {
          useFormMethods.reset({});
        },
        large: true,
        backdrop: isFormDirty ? 'static' : undefined,
        title: `${projectUserProfile && isPositiveNumber(projectUserProfile.id) ? 'Edit' : 'Add'} Member`,
        onRenderBody: (bodyExtras?: ModalBodyExtras) => {
          return (
            <MemberForm
              setIsFormDirty={setIsFormDirty}
              projectUserProfile={projectUserProfile}
              shouldIncludeHiddenRoles={shouldIncludeHiddenRoles}
              {...memberFormProps}
            />
          );
        },
        onRenderFooter: (onHide?: () => void, bodyExtras?: ModalBodyExtras) => {
          const onClickCancel = () => {
            if (confirmUnsavedPrompt() && onHide) {
              onHide();
            }
          };
          const hasNoProfileInState = !formStateProjectUserProfileId && !projectUserProfile?.id;
          return (
            <>
              {!(projectUserProfile?.userProfile?.isResigned || useFormMethods.getValues('userProfile.isResigned')) && (
                <LoadingButton
                  disabled={hasNoProfileInState}
                  type={ComponentType.Primary}
                  onClick={() => {
                    onConfirm(projectUserProfile);
                  }}
                  isLoading={isSubmitLoading}
                  isError={isSubmitError}
                  isSuccess={isSubmitSuccess}
                  timeToResetSuccessState={3000}
                  timeToResetErrorState={5000}
                  prefixIconName="FolderCheck"
                >
                  {hasNoProfileInState ? 'Search' : 'Save'}
                </LoadingButton>
              )}
              <Button
                prefixIconName="ChevronLeft"
                type={ComponentType.Secondary}
                onClick={onClickCancel}
                style={{ margin: '10px' }}
              >
                Cancel
              </Button>
            </>
          );
        },
      })
    );
  };
  return {
    openPortal,
    closePortal: closePortal.current,
    renderModal: renderMemberModal,
  };
};
